import HttpResponseError from './HttpResponseError';

export type SubscriptionPlanType = 'standard' | 'free' | 'community' | 'partner' | 'build-default';

export interface SubscriptionPlan {
  type: SubscriptionPlanType;
  features: string[];
  is_free_tier: boolean;
}

export interface BootstrapData {
  /**
   * CSRF token to use for non-GET HTTP requests.
   */
  csrf_token: string;
  /**
   * Environment ID such as "int", "dev", "local", etc.
   */
  environment: string;
  /**
   * Sentry DSN, if configured.
   */
  sentry_dsn: string | '';
  /**
   * ID of the currently authenticated user.
   */
  user_id: string;
  /**
   * Display name of the currently authenticated user.
   */
  user_display_name: string;
  /**
   * Tenant ID.
   */
  tenant_id: string;
  /*
   * Tenant scoped user-id is currently "email" because Lobby has chosen it.
   * Once the external uid is changed to something else, change it as the
   * value for "tenant_uid".
   */
  tenant_uid: string;
  /**
   * URL of the Lobby for the current environment.
   */
  lobby_url: string;
  /**
   * URL to link to the feedback survey.
   */
  feedback_survey_url: string;
  /**
   * URL to link to the privacy statement.
   */
  privacy_statement_url: string;
  /**
   * Features enabled on the frontend. This includes feature flags,
   * features from feature variables, as well as subscription-enabled features.
   */
  enabled_features: string[];
  /**
   * Subscription plan
   */
  subscription_plan: SubscriptionPlan;
  /**
   * Customer tenant subdomain. This is the same as found from the URL
   */
  subdomain: string;
  /**
   * CloudFoundry domain where we deploy our MTA apps
   */
  cfdomain: string;

  tenant: {
    /**
     * Whether tenant is ready to deploy VCF runners
     */
    isReadyForVCFDeploys: boolean;
  };

  /**
   * Preferences of the currently authenticated user.
   */
  user_preferences?: {
    dateFormat: 'medium' | 'short' | 'long';
    timeFormat: '12' | '24';
    theme: 'sap_horizon' | 'sap_horizon_dark' | 'sap_horizon_hcw' | 'sap_horizon_hcb';
    language: string;
  };

  /**
   * User id that is not personally identifiable and thus can be sent to Sentry.
   */
  user_id_for_sentry: string;
}

let bootstrapRequest: Promise<BootstrapData> | undefined;

async function bootstrapData(): Promise<BootstrapData> {
  if (bootstrapRequest) {
    return bootstrapRequest;
  }
  // Make URL absolute at this point, because passing a relative URL to fetch in Vitest tests (with Node fetch + jsdom) won't work
  const url = '/ui-bootstrap/';
  bootstrapRequest = fetch(typeof window !== 'undefined' ? `${window.location.origin}${url}` : url, {
    headers: {
      'x-requested-with': 'XMLHttpRequest',
    },
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    throw new HttpResponseError('Failed to fetch bootstrap data', response);
  });
  return bootstrapRequest;
}

export async function getCsrfToken() {
  const data = await bootstrapData();
  return data.csrf_token;
}

export function clearBootstrapDataCache() {
  bootstrapRequest = undefined;
}

export default bootstrapData;
